import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  Search, Package, X, Check, Loader2, Sparkles, FolderTree, Star
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MasterCatalogSelector (v2)
 * Fetches universal dynamic templates from /api/master-catalog/templates/browse
 * and calls /api/master-catalog/templates/:templateId/adopt to get defaultFieldValues.
 */
const MasterCatalogSelector = ({
  onSelectTemplate,
  productType = 'product',
  category = '',
  subcategory = '',
  isOpen = false,
  onClose,
}) => {
  const { toast } = useAuth() || {};
  const { toast: toastFn } = useToast();
  const showToast = toastFn || toast;

  const [loading, setLoading] = useState(true);
  const [adopting, setAdopting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  // Pre-seed search with subcategory from form so vendor sees relevant templates immediately
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, searchQuery, page, productType, category, subcategory]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 12,
        isActive: true,
      });
      // Normalize listingType: subscriptionSchema uses "Subscriptions" but templates are stored as "subscription"
      const normalizedType = productType === 'Subscriptions' ? 'subscription' : productType;
      if (normalizedType) params.set('listingType', normalizedType);
      // Use selected category from form as a filter, subcategory as search
      if (category) params.set('category', category);
      if (searchQuery) {
        params.set('subcategory', searchQuery);
      } else if (subcategory) {
        // Auto-pre-filter by form's current subcategory when no manual search
        params.set('subcategory', subcategory);
      }

      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API}/api/master-catalog/templates/browse?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load templates');
      const data = await res.json();
      setTemplates(data.templates || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast && showToast({
        title: 'Error',
        description: 'Failed to load catalog templates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdoptTemplate = async (tpl) => {
    setAdopting(true);
    setSelectedTemplate(tpl);
    try {
      const token = sessionStorage.getItem('token');
      const templateId = tpl.templateId || tpl.id;
      const res = await fetch(
        `${API}/api/master-catalog/templates/${templateId}/adopt`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (!res.ok) throw new Error('Failed to adopt template');
      const data = await res.json();

      // master_templates adopt returns: { defaultFieldValues, category, subcategory, listingType, variants, customOptions }
      const rawDfv = data.defaultFieldValues || {};
      const listingType = data.listingType || productType || 'product';
      const tplCategory = data.category || tpl.category || '';
      const tplSubcategory = data.subcategory || tpl.subcategory || '';
      const tplVariants = data.variants || rawDfv.variants || [];

      // ── Flatten nested 'data' object ──
      // When admin saves a template via UniversalSinglePageForm, form fields are nested
      // inside defaultFieldValues.data.{} — we must extract them to top-level so they
      // map directly to form field names (price, bookingMode, duration, etc.)
      const { data: nestedData, listing_type: _lt, ...topLevel } = rawDfv;
      const dfv = { ...topLevel };
      if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
        Object.entries(nestedData).forEach(([key, value]) => {
          // Top-level fields take priority; don't overwrite them
          if (dfv[key] === undefined || dfv[key] === null || dfv[key] === '') {
            dfv[key] = value;
          }
        });
      }

      showToast && showToast({
        title: 'Template Applied!',
        description: `"${tpl.templateName || tpl.name}" pre-filled your form.`,
      });

      // ── Schema-aware field mapping per listing type ──
      // Each listing type uses different field names for category, pricing, tax, etc.
      const FIELD_MAP = {
        product: {
          categoryField: 'category',
          subcategoryField: 'subcategory',
          priceField: 'price',
          comparePriceField: 'comparePrice',
          hsnField: 'hsnCode',
        },
        service: {
          categoryField: 'category',
          subcategoryField: 'subcategory',
          priceField: 'price',
          comparePriceField: 'compareAtPrice',
          hsnField: 'hsn_sac_code',
        },
        subscription: {
          categoryField: 'subscriptionType',
          subcategoryField: 'subcategory',
          priceField: 'unit_price',
          basePlanPriceField: 'base_plan_price',
          hsnField: 'hsn_sac_code',
        },
        rental: {
          categoryField: 'rentalCategory',
          subcategoryField: 'rentalSubcategory',
          hsnField: 'hsnCode',
        },
        event: {
          categoryField: 'eventType',
          priceField: 'ticketPrice',
          hsnField: 'hsn_sac_code',
        },
        classified: {
          categoryField: 'category',
          priceField: 'price',
        },
        volunteering: {
          categoryField: 'sevaType',
        },
      };

      // Normalize listing type key (handle "Subscriptions" → "subscription", etc.)
      const normalizedType = listingType.toLowerCase().replace(/s$/, '');
      const fm = FIELD_MAP[normalizedType] || FIELD_MAP.product;

      // Build mapped data — start with category/subcategory using correct field names
      const mapped = {
        // Category — mapped to the correct field name for this listing type
        ...(fm.categoryField && tplCategory ? { [fm.categoryField]: tplCategory } : {}),
        ...(fm.subcategoryField && tplSubcategory ? { [fm.subcategoryField]: tplSubcategory } : {}),

        // Variants from template
        ...(tplVariants.length > 0 ? { variants: tplVariants, hasVariants: true } : {}),
        // Variant combinations from template or dfv
        ...(dfv.variantCombinations?.length > 0 ? { variantCombinations: dfv.variantCombinations, hasVariants: true } : {}),

        // Metadata
        _templateId: templateId,
        _templateName: tpl.templateName || tpl.name || '',
      };

      // Apply defaultFieldValues — these are the actual form field values from the template
      // Map known generic names to the correct schema-specific names
      const priceAliases = ['basePrice', 'base_price', 'price'];
      // Map backend snake_case to frontend camelCase
      const CAMEL_MAP = {
        'booking_mode': 'bookingMode',
        'pricing_model': 'pricingModel',
        'compare_at_price': 'comparePrice',
        'hsn_code': 'hsnCode',
        'hsn_sac_code': 'hsnCode',
        'charge_tax': 'chargeTax',
        'has_variants': 'hasVariants',
        'variant_combinations': 'variantCombinations',
        'delivery_frequency': 'deliveryFrequency',
        'subscription_type': 'subscriptionType',
        'rental_category': 'rentalCategory',
        'rental_subcategory': 'rentalSubcategory',
        'rental_template': 'rentalTemplate',
        'session_duration': 'sessionDuration',
        'slot_duration': 'slotDuration',
        'has_logistics_charges': 'hasLogisticsCharges',
        'base_plan_price': 'basePlanPrice',
        'unit_price': 'unit_price',
      };
      for (const [k, v] of Object.entries(dfv)) {
        if (v == null || v === '') continue;
        // Skip internal/structural keys already handled
        if (['variants', 'variantCombinations', 'variant_combinations', 'taxonomyAttributes'].includes(k)) continue;

        // Resolve the actual key (snake_case → camelCase)
        const resolvedKey = CAMEL_MAP[k] || k;

        // Map basePrice/base_price to the correct price field for this listing type
        if (priceAliases.includes(k) && fm.priceField) {
          mapped[fm.priceField] = v;
          // For subscriptions, also set base_plan_price
          if (fm.basePlanPriceField) mapped[fm.basePlanPriceField] = v;
          continue;
        }

        // Map hsn_code/hsnCode to the correct field
        if ((k === 'hsn_code' || k === 'hsnCode' || k === 'hsn_sac_code') && fm.hsnField) {
          mapped[fm.hsnField] = v;
          mapped.chargeTax = true;
          continue;
        }

        // Everything else passes through with resolved camelCase key
        mapped[resolvedKey] = v;
      }

      // Title/description from the template card if not in defaultFieldValues
      if (!mapped.title) mapped.title = tpl.templateName || tpl.name || '';
      if (!mapped.description) mapped.description = tpl.templateDescription || '';

      // Remove empty/null values
      const cleaned = {};
      const keepEmptyArrays = new Set(['tags', 'variants', 'add_ons', 'media']);
      for (const [k, v] of Object.entries(mapped)) {
        if (v === undefined || v === null || v === '') continue;
        if (Array.isArray(v) && v.length === 0 && !keepEmptyArrays.has(k)) continue;
        cleaned[k] = v;
      }

      onSelectTemplate(cleaned);
      onClose();
    } catch (error) {
      showToast && showToast({
        title: 'Error',
        description: error.message || 'Failed to apply template',
        variant: 'destructive',
      });
    } finally {
      setAdopting(false);
      setSelectedTemplate(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Quick-Start Templates</h2>
              <p className="text-sm text-gray-600">
                {category || subcategory
                  ? `Showing templates for: ${[category, subcategory].filter(Boolean).join(' > ')}`
                  : 'Select a template to pre-fill your listing form'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-master-catalog">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="p-4 border-b bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by subcategory..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="pl-10"
              data-testid="catalog-search"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No templates found</p>
              <p className="text-sm text-gray-400 mt-1">
                {searchQuery
                  ? 'Try a different search term.'
                  : (category || subcategory)
                    ? `No templates for "${[category, subcategory].filter(Boolean).join(' > ')}" yet. Clear search to see all templates.`
                    : 'No templates have been created by admin yet.'}
              </p>
              {(category || subcategory) && !searchQuery && (
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setSearchQuery(' ')}>
                  Show all templates
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((tpl) => (
                  <Card
                    key={tpl.templateId}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedTemplate?.templateId === tpl.templateId ? 'ring-2 ring-purple-500' : ''
                    }`}
                    onClick={() => setSelectedTemplate(tpl)}
                    data-testid={`template-card-${tpl.templateId}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {tpl.templateImage ? (
                            <img src={tpl.templateImage} alt={tpl.templateName} className="h-full w-full object-cover rounded-lg" />
                          ) : (
                            <Package className="h-6 w-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{tpl.templateName}</h3>
                          {tpl.subcategory && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <FolderTree className="h-3 w-3" />
                              {tpl.category} {tpl.subcategory ? `> ${tpl.subcategory}` : ''}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge variant="outline" className="text-xs capitalize">{tpl.listingType}</Badge>
                            {tpl.templatePriority === 1 && (
                              <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                                <Star className="h-3 w-3 mr-0.5" /> Featured
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {tpl.templateDescription && (
                        <p className="text-xs text-gray-500 mt-3 line-clamp-2">{tpl.templateDescription}</p>
                      )}

                      <div className="mt-3 pt-3 border-t flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {tpl.adoptionCount || 0} vendor{tpl.adoptionCount !== 1 ? 's' : ''} using this
                        </span>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleAdoptTemplate(tpl); }}
                          disabled={adopting && selectedTemplate?.templateId === tpl.templateId}
                          data-testid={`use-template-${tpl.templateId}`}
                        >
                          {adopting && selectedTemplate?.templateId === tpl.templateId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Check className="h-3 w-3 mr-1" /> Use</>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {total > 12 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    Showing {(page - 1) * 12 + 1}–{Math.min(page * 12, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 12 >= total}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            Templates pre-fill your form with sensible defaults. You can customize everything after applying.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MasterCatalogSelector;
